import {Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { FillChartData } from '../../../../@domain/line-chart-data';
import { Chart } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import {ApiBaseService, VendorsService} from '../../../../../../projects/vendors/src/public-api';
import { differenceInCalendarDays } from 'date-fns';
import { ApiEnum } from '../../../../@application/enum/api.enum';
import {Subscription} from "rxjs";
import {BrandingService} from '../../../../@application/services/branding.service';

@Component({
    selector: 'app-sales',
    templateUrl: './sales.component.html',
    styleUrls: ['./sales.component.scss'],
    standalone: false
})
export class SalesComponent implements OnInit,OnDestroy{

  myChart: any;
  @Input() role!: string;
  salesHistory: any[] = [];
  dates: string[] = [];
  data: FillChartData[] = [];
  elementRef = inject(ElementRef);
  toDay = new Date();
  startValue: Date | null = null;
  endValue: Date | null = null;
  @ViewChild('endDatePicker') endDatePicker!: NzDatePickerComponent;
  apiBaseService = inject(ApiBaseService);
  vendorsService = inject(VendorsService);
  private brandingService = inject(BrandingService);
  newOrderSubscription!: Subscription;
  orderCloseSubscription!: Subscription;
  orderStatusSubscription!: Subscription;

  constructor() { }

  ngOnInit(): void {
    this.dataSubscriptions();
    this.endValue = this.toDay;
    this.startValue = new Date(this.toDay.getTime() - 7 * 24 * 60 * 60 * 1000);
    if (this.startValue && this.endValue) {
      this.getSalesHistory(this.formatDate(this.startValue), this.formatDate(this.endValue));
    }
  }

  private dataSubscriptions() {
    this.newOrderSubscription = this.vendorsService.newOrderSubject.subscribe({
      next: newOrder => {
        if (newOrder)
          this.getSalesHistory(this.formatDate(this.startValue!), this.formatDate(this.endValue!));
      }
    })
    this.orderCloseSubscription = this.vendorsService.orderCloseSubject.subscribe({
      next: orderClose => {
        if (orderClose)
          this.getSalesHistory(this.formatDate(this.startValue!), this.formatDate(this.endValue!));
      }
    })
    this.orderStatusSubscription = this.vendorsService.orderStatusSubject.subscribe({
      next: orderStatus => {
        if (orderStatus)
          this.getSalesHistory(this.formatDate(this.startValue!), this.formatDate(this.endValue!));
      }
    })
  }

  disabledStartDate = (startValue: Date): boolean => {
    if (!startValue || !this.endValue) {
      return differenceInCalendarDays(startValue, this.toDay) > 0;
    }
    // Allow start date to be equal to the end date
    return startValue.getTime() > this.endValue.getTime();
  };

  disabledEndDate = (endValue: Date): boolean => {
    if (!endValue || !this.startValue) {
      return differenceInCalendarDays(endValue, this.toDay) > 0;
    }
    // Allow end date to be equal to the start date
    return endValue.getTime() < this.startValue.getTime() || endValue.getTime() > this.toDay.getTime();
  };


  onDateAdded() {
    if (this.startValue && this.endValue) {
      this.getSalesHistory(this.formatDate(this.startValue), this.formatDate(this.endValue));
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  handleStartOpenChange(open: boolean): void {
    if (!open && this.endValue === null || undefined) {
      this.endDatePicker.open();
    }
  }

  private createChart(): void {
    if (this.myChart) {
      this.myChart.destroy();
    }
    const ctx = this.elementRef.nativeElement.querySelector('#myChart') as HTMLCanvasElement;

    this.myChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.dates,
        datasets: this.data
      },
      options: {
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          datalabels: {
            display: false,
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                xMin: this.dates[this.dates.length - 1],
                xMax: this.dates[this.dates.length - 1],
                borderColor: 'rgb(255, 99, 132)',
                borderWidth: 2,
                label: {
                  content: 'Today',
                }
              }
            }
          }
        },
        aspectRatio: 6
      }
    });

  }

  private getSalesHistory(startDate: string, endDate: string): void {
    this.apiBaseService.GET<any[]>([ApiEnum.RestaurantModule,ApiEnum.OrderBill,ApiEnum.DashBoard,ApiEnum.RevenueProfit,startDate,endDate])
    .subscribe({
      next: (response) => {
        this.salesHistory = response;
        this.dates = this.salesHistory.map(item => item.date.split('T')[0]);
        const revenues = this.salesHistory.map(item => item.totalRevenue);
        const profits = this.salesHistory.map(item => item.totalProfit);

        this.data = [
          {
            label: 'Total Revenue',
            data: revenues,
            fill: false,
            borderColor: '#42A5F5',
            tension: 0.3,
            backgroundColor: 'rgba(66, 165, 245, 0.2)'
          },
          {
            label: 'Total Profit',
            data: profits,
            fill: false,
            borderColor: this.brandingService.getBrandColor(),
            tension: 0.3,
            backgroundColor: this.brandingService.getBrandColorAlpha(0.2)
          }
        ];

        if (this.role === 'Manager') {
          this.data = this.data.filter(item => item.label === 'Total Revenue');
        }

        this.createChart();
      }
    });
  }

  ngOnDestroy(): void {
    this.newOrderSubscription.unsubscribe();
    this.orderCloseSubscription.unsubscribe();
    this.orderStatusSubscription.unsubscribe();
  }
}
